import React from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { BsClock } from "react-icons/bs";
import { DragableList, DragHandle } from '../dragableList';
import { CustomTooltip } from '../../reusableItems/tooltips';
import { dateFormatter } from '../../../../supplementary_components/supplementaryMethods';
import { addNotification } from '../../../../supplementary_components/store';




const StopCard = styled.div`
    width:99.8%;
    margin: 1rem 0 0 0;
    padding: 0.8rem 0.5rem 0.8rem 1.2rem;
    box-shadow: 0 0 0 0.035rem var(--light-gray);
    border-radius: 8px;
    display: grid;
    gap: 0.5rem;
    grid-template-columns: 1.5fr 0.5fr 35% 3.5%;
    color:var(--dark-gray);
    font-weight:500;
    background-color:var(--bg);
    div{
        div{
            padding: 0.12rem 0; 
        }
    }
    background: ${props => props.$stopStatus === "inProgress" ? "var(--in-progress-background)" : (props => props.$stopStatus === "completed" ? "var(--completed-background)" : '')};
    
`;
const LeftSide = styled.div`
    width:max-content;
    max-width:100%;
    .addressBlock{
        display:flex;
        flex-direction:column;
        width:max-content;
        max-width:100%;
        &>div{
            width:max-content;
            max-width:100%;
        }
        &.copyable{
            cursor:pointer;
            &:hover,
            &:hover *{
                color:var(--main-color);
            }
        }
    }
    .cityName{
        font-weight:bold;
        font-size: ${props=>props.$smallerfont ? "1.1rem" : "1.2rem" };
        margin: 0.2rem 0; 
    }
`;
const Center = styled.div`
    margin: 0.2rem 0; 
    color:var(--little-light-gray);
    .copyableRef{
        display:inline-block;
        width:max-content;
        cursor:pointer;
        &:hover{
            color:var(--main-color);
        }
    }
`
const RightSide = styled.div`
    text-align:right;
    .loadingType{
        color:var(--light-gray);
        font-size:0.8rem;
        margin-bottom:0.5rem;
    }
`;
const CheckTimeIcon = styled.span`
    display:inline-flex;
    align-items:center;
    vertical-align:middle;
    margin-top:-0.1rem;
    margin-left:0.3rem;
    position:relative;
    cursor:default;
    color:var(--little-light-gray);
    line-height:1;
    svg{
        width:0.9em;
        height:0.85em;
        display:block;
    }
    .tooltip{
        position:absolute;
        right:-1rem;
        left:auto;
        top:100%;
        &>.tooltipContentWrapper{
            right:0;
            left:auto;
            &>span{
                right:0;
                left:auto;
                margin-top:0.55rem;
                margin-left:0;
                padding:0.6rem 1.3rem;
                .arrowEffect{
                    left:auto;
                    right:1rem;
                    top:-0.45rem;
                }
                .tooltipContentText,
                .tooltipContentText *{
                    font-size:0.85rem !important;
                    font-weight:600;
                    line-height:1.35;
                }
            }
        }
    }
    :hover {
        .tooltip{
            visibility: visible;
            transition-property: visibility;
            transition-delay: 0.3s;
        }
    }
`;
const CheckTimeTipBody = styled.div`
    display:grid;
    grid-template-columns:max-content max-content;
    column-gap:0.35rem;
    row-gap:0.25rem;
    align-items:baseline;
    color:var(--gray);
    .left{
        text-align:right;
        white-space:nowrap;
    }
    .right{
        text-align:left;
        white-space:nowrap;
    }
    .timeDelta{
        &.early,
        &.shortStop{
            color:var(--main-color);
        }
        &.late,
        &.longStop{
            color:var(--alert-color);
        }
    }
`;

export const StopsWithStatuses = ({data, updateData, smallerFont})=>{
    function finishDrag(oldIndex, newIndex){
        if (oldIndex !== newIndex){
            const elm = data.splice(oldIndex, 1)[0];
            data.splice(newIndex, 0, elm);
            let lastDetectedDate = null;
            for (const [index, stop] of data.entries()) {
            // for (let stop of data){
                if (lastDetectedDate){
                    if ((new Date(stop.apptDate) < lastDetectedDate) || 
                        (index === 0 && stop.stopType === "DEL") ||
                        (index === data.length-1 && stop.stopType === "PU")  ){//
                        const elm = data.splice(newIndex, 1)[0];
                        data.splice(oldIndex, 0, elm);
                        
                        return false;
                    }
                }
                lastDetectedDate = new Date(stop.apptDate);
            }
            updateData(data);
        }        
        return true;
    }
    const ListOfStops = () => <>{data.map((stopData, index)=>{
        const stopLatLng = getStopLatLng(stopData);
        const checkInDelta = stopData['checkIn'] && stopData['zonedDateTime']
            ? getCheckInDelta(stopData['checkIn'], stopData['zonedDateTime'])
            : null;
        const checkOutDuration = stopData['checkIn'] && stopData['checkOut']
            ? getCheckOutDuration(stopData['checkIn'], stopData['checkOut'])
            : null;
        return <StopCard key={`${stopData['address']['id']}${index}`} $stopStatus={stopData['status']} id='loadStops' data-index={index}>
            <LeftSide $smallerfont={smallerFont}>
                <div
                    className={`addressBlock${stopLatLng ? ' copyable' : ''}`}
                    title={stopLatLng ? 'Copy coordinates' : undefined}
                    role={stopLatLng ? 'button' : undefined}
                    tabIndex={stopLatLng ? 0 : undefined}
                    onClick={stopLatLng ? (e) => {
                        e.stopPropagation();
                        copyToClipboard(stopLatLng, 'Coordinates copied', 'Failed to copy coordinates');
                    } : undefined}
                    onKeyDown={stopLatLng ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            copyToClipboard(stopLatLng, 'Coordinates copied', 'Failed to copy coordinates');
                        }
                    } : undefined}
                >
                    {ItemWithTag(stopData['address']['companyName'])}
                    {ItemWithTag(stopData['address']['addressLine1'])}
                    {ItemWithTag(stopData['address']['addressLine2'])}
                    {ItemWithTag(`${stopData['address']['city']}, ${stopData['address']['state']} ${stopData['address']['postalCode']}`, 'cityName')}
                </div>
            </LeftSide>
            <Center>
                {stopData["refNumber"] ? (
                    <span
                        className='copyableRef'
                        title='Copy ref #'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(stopData["refNumber"], 'Ref # copied', 'Failed to copy ref #');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                copyToClipboard(stopData["refNumber"], 'Ref # copied', 'Failed to copy ref #');
                            }
                        }}
                    >
                        {`Ref#: ${stopData["refNumber"]}`}
                    </span>
                ) : null}
            </Center>
            <RightSide>
                <div className='loadingType'>
                    {`${stopData["stopType"] === "PU" ? "Pickup" : stopData["stopType"] === "DEL" ? "Delivery" : "Drop&Pick"} (${stopData['loadingType']})`}
                    {(stopData['checkIn'] || stopData['checkOut']) ? (
                        <CheckTimeIcon>
                            <BsClock />
                            <CustomTooltip
                                info={<CheckTimeTipBody style={{fontSize: '0.7rem'}}>
                                    {stopData['checkIn'] ? (
                                        <>
                                            <span className='left'>{`In: ${formatCheckTime(stopData['checkIn'])}`}</span>
                                            <span className='right'>
                                                {checkInDelta ? (
                                                    <>
                                                        
                                                        <span className={`timeDelta ${checkInDelta.earlier ? 'early' : 'late'}`}>
                                                            {checkInDelta.label}
                                                        </span>
                                                    </>
                                                ) : null}
                                            </span>
                                        </>
                                    ) : null}
                                    {stopData['checkOut'] ? (
                                        <>
                                            <span className='left'>{`Out: ${formatCheckTime(stopData['checkOut'])}`}</span>
                                            <span className='right'>
                                                {checkOutDuration ? (
                                                    <>
                                                        
                                                        <span className={`timeDelta ${checkOutDuration.longStop ? 'longStop' : 'shortStop'}`}>
                                                            {checkOutDuration.label}
                                                        </span>
                                                    </>
                                                ) : null}
                                            </span>
                                        </>
                                    ) : null}
                                </CheckTimeTipBody>}
                            />
                        </CheckTimeIcon>
                    ) : null}
                </div>
                {ItemWithTag(stopData['apptDate'])}{ItemWithTag(stopData['apptTime'])} 
                
            </RightSide>
            <DragHandle handleClassName='loadDragHandle' />
        </StopCard> 
    })} </>;
    return <DragableList component={ListOfStops} handleClassName='loadDragHandle' finishDrag={finishDrag} />
}
const getStopLatLng = (stopData) => {
    const value = stopData?.latLng || stopData?.address?.latLng;
    if (value == null) return null;
    const text = String(value).trim();
    return text || null;
};

const copyToClipboard = (text, successMessage, errorMessage) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text)).then(() => {
        addNotification({ message: successMessage, type: 'success' });
    }).catch(() => {
        addNotification({ message: errorMessage, type: 'alert' });
    });
};

const formatCheckTime = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return iso;
    const [, y, mo, d, h24, min] = m;
    const hour = parseInt(h24, 10);
    const minute = parseInt(min, 10);
    const ap = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    const intermediate = `${y}-${mo}-${d} ${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ap}`;
    const { date, time } = dateFormatter(intermediate);
    if (date && time) {
        const md = date.match(/^(\d{2})\/(\d{2})\/\d{2}$/);
        return md ? `${md[1]}/${md[2]} ${time}` : `${date} ${time}`;
    }
    return `${mo}/${d} ${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ap}`;
};

/** Window format is `/HH:mm:ss` + ISO start; otherwise ISO zoned datetime. */
const parseStopZonedDateTime = (zonedDateTime) => {
    if (!zonedDateTime || typeof zonedDateTime !== 'string') return null;
    let raw = zonedDateTime.trim();
    if (!raw) return null;
    if (raw.charAt(0) === '/' && raw.length > 9) {
        raw = raw.substring(9);
    }
    const dt = DateTime.fromISO(raw, { setZone: true });
    return dt.isValid ? dt : null;
};

const formatDurationLabel = (totalMinutes, { withSign = false, earlier = false } = {}) => {
    const abs = Math.abs(totalMinutes);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    let label = '';
    if (withSign) label += earlier ? '+' : '-';
    if (h > 0) label += `${h}h`;
    if (m > 0 || h === 0) {
        if (h > 0) label += ' ';
        label += `${m}m`;
    }
    return label;
};

/** Earlier than appointment → +Xh Ym; later → -Xh Ym. */
const getCheckInDelta = (checkIn, zonedDateTime) => {
    if (!checkIn || !zonedDateTime) return null;
    const scheduled = parseStopZonedDateTime(zonedDateTime);
    const actual = DateTime.fromISO(checkIn, { setZone: true });
    if (!scheduled || !actual.isValid) return null;
    const diffMins = Math.round(scheduled.diff(actual, 'minutes').minutes);
    if (diffMins === 0) return null;
    const earlier = diffMins > 0;
    return {
        label: formatDurationLabel(diffMins, { withSign: true, earlier }),
        earlier,
    };
};

/** Time on site from check-in to check-out; >2h is longStop. */
const getCheckOutDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;
    const start = DateTime.fromISO(checkIn, { setZone: true });
    const end = DateTime.fromISO(checkOut, { setZone: true });
    if (!start.isValid || !end.isValid) return null;
    const diffMins = Math.round(end.diff(start, 'minutes').minutes);
    if (diffMins < 0) return null;
    return {
        label: formatDurationLabel(diffMins),
        longStop: diffMins > 120,
    };
};

const ItemWithTag = (data, className) =>{
    
    try {
        if (data === null || data === undefined)return null;
        if(typeof(data) === 'string'){
            data = data.replaceAll("undefined", '');
        }
        return <div className={className}>{data}</div>
    } catch (error) {
    }
   
}
